import React from "react";
import { Church, CalendarClock, Trash2, Check, X, ImageIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { resolveMediaUrl } from "@/components/FileUpload";
import { BottomSheet } from "@/components/story/StorySheets";
import { localeOf } from "@/components/story/storyEditorUtils";
import { avatarSrc } from "@/lib/defaults";
import { AudienceOptions } from "@/components/AudienceOptions";
import { MusicSection } from "./PostMediaPreviews";

// Feuilles du composeur de publication plein écran (téléphone) — même BottomSheet que l'éditeur de story.
import { maxDatetimeLocal } from "@/lib/scheduleLimits";

const row = "w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left text-sm font-semibold bg-white/5 active:bg-white/15 transition-colors";

// Lot 649 — « Qui peut voir ta publication ? » : Public · Abonnés · Abonnés mutuels · Suivis · Moi uniquement.
export const AudienceSheet = ({ open, onClose, value, onPick, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("audience.title", "Qui peut voir ta publication ?")} testId="post-audience-sheet" maxH="70dvh">
    <AudienceOptions value={value} onPick={onPick} t={t} tone="dark" />
  </BottomSheet>
);

// « Publier en tant que » : Moi ou l'une de mes églises (prédicateur).
export const PublishAsSheet = ({ open, onClose, user, churches, publishAs, onPick, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("composer.publish_as", "Publier en tant que")} testId="post-fs-publish-as-sheet" maxH="60dvh">
    <div className="space-y-1.5 mt-1">
      <button type="button" onClick={() => onPick("me")} className={row} aria-pressed={publishAs === "me"} data-testid="composer-publish-as-me">
        <img src={avatarSrc(user)} alt="" className="h-9 w-9 rounded-full object-cover shrink-0 border-2" style={{ borderColor: "var(--yalola-primary)" }} />
        <span className="flex-1 min-w-0 truncate">{t("composer.publish_as_me", "Moi (Compte personnel)")}</span>
        {publishAs === "me" && <Check size={18} style={{ color: "var(--yalola-primary)" }} />}
      </button>
      {churches.map((c) => (
        <button key={c.id} type="button" onClick={() => onPick(c.id)} className={row} aria-pressed={publishAs === c.id} data-testid={`composer-publish-as-${c.id}`}>
          {c.logo_url
            ? <img src={resolveMediaUrl(c.logo_url)} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
            : <span className="h-9 w-9 rounded-full grid place-items-center bg-sky-500 text-white shrink-0"><Church size={16} /></span>}
          <span className="flex-1 min-w-0"><span className="block truncate">{c.name}</span>{c.city && <span className="block text-[11px] font-normal opacity-60 truncate">{c.city}</span>}</span>
          {publishAs === c.id && <Check size={18} style={{ color: "var(--yalola-primary)" }} />}
        </button>
      ))}
    </div>
  </BottomSheet>
);

// Menu ⋯ : programmer, retirer les médias, abandonner.
export const PostMoreSheet = ({ open, onClose, onSchedule, scheduledAt, hasMedia, onRemoveAllMedia, onDiscard, lang, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.more_title", "Options")} testId="post-fs-more-sheet" maxH="60dvh">
    <div className="space-y-1.5 mt-1">
      <button type="button" onClick={onSchedule} className={row} data-testid="post-fs-more-schedule">
        <CalendarClock size={18} style={{ color: "var(--yalola-primary)" }} />
        <span className="flex-1">{t("post.schedule", "Programmer la publication")}</span>
        {scheduledAt && <span className="text-[11px] font-normal opacity-70">{new Date(scheduledAt).toLocaleString(localeOf(lang), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
      </button>
      {hasMedia && (
        <button type="button" onClick={onRemoveAllMedia} className={row} data-testid="post-fs-more-remove-media">
          <ImageIcon size={18} className="opacity-80" /> {t("composer.fs_remove_all_media", "Retirer tous les médias")}
        </button>
      )}
      <button type="button" onClick={onDiscard} className={`${row} text-red-300`} data-testid="post-fs-more-discard">
        <Trash2 size={18} /> {t("story.discard", "Abandonner")}
      </button>
    </div>
  </BottomSheet>
);

const minDatetimeLocal = () => {
  const d = new Date(Date.now() + 5 * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Programmation : interrupteur + date/heure.
export const PostScheduleSheet = ({ open, onClose, scheduledAt, setScheduledAt, t }) => {
  const on = !!scheduledAt;
  return (
    <BottomSheet open={open} onClose={onClose} title={t("post.schedule", "Programmer la publication")} testId="post-fs-schedule-sheet" maxH="50dvh">
      <div className="flex items-center justify-between py-2">
        <span className="text-sm">{t("story.scheduled_badge", "Programmée")}</span>
        <Switch checked={on} onCheckedChange={(v) => setScheduledAt(v ? minDatetimeLocal() : "")} data-testid="composer-schedule-switch" />
      </div>
      {on && (
        <input type="datetime-local" value={scheduledAt} min={minDatetimeLocal()} max={maxDatetimeLocal()} onChange={(e) => setScheduledAt(e.target.value)} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white" style={{ colorScheme: "dark" }} data-testid="composer-schedule-input" />
      )}
      <button type="button" onClick={onClose} className="mt-4 w-full h-11 rounded-full text-sm font-bold text-white flex items-center justify-center gap-1.5" style={{ background: "var(--yalola-primary)" }} data-testid="post-fs-schedule-done"><Check size={16} /> {t("story.done", "Terminé")}</button>
    </BottomSheet>
  );
};

// Fermeture avec contenu : garder le brouillon, abandonner, ou continuer.
export const PostCloseSheet = ({ open, onClose, onKeep, onDiscard, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("composer.fs_close_title", "Fermer la publication ?")} testId="post-fs-close-sheet" maxH="50dvh">
    <div className="space-y-1.5 mt-1">
      <button type="button" onClick={onKeep} className={row} data-testid="post-fs-close-keep"><Check size={18} style={{ color: "var(--yalola-primary)" }} /> {t("story.keep_draft_close", "Garder le brouillon et fermer")}</button>
      <button type="button" onClick={onDiscard} className={`${row} text-red-300`} data-testid="post-fs-close-discard"><Trash2 size={18} /> {t("story.discard", "Abandonner")}</button>
      <button type="button" onClick={onClose} className={row} data-testid="post-fs-close-continue"><X size={18} className="opacity-70" /> {t("story.keep_editing", "Continuer")}</button>
    </div>
  </BottomSheet>
);

// Musique de fond (média unique) : feuille claire comme dans l'éditeur de story.
export const PostMusicSheet = ({ open, onClose, t, ...music }) => (
  <BottomSheet open={open} onClose={onClose} title={t("composer.fs_music", "Musique")} light testId="post-fs-music-sheet" maxH="80dvh">
    <MusicSection {...music} t={t} testId="composer-music" />
    <button type="button" onClick={onClose} className="mt-4 w-full h-11 rounded-full text-sm font-bold text-white flex items-center justify-center gap-1.5" style={{ background: "var(--yalola-primary)" }} data-testid="post-fs-music-done"><Check size={16} /> {t("story.done", "Terminé")}</button>
  </BottomSheet>
);
